import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { LogOut, KeyRound, Sun, Moon, MonitorSmartphone, User as UserIcon, Building2, Check, RefreshCw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { usePortalAnchorRect } from "../lib/usePortalAnchorRect";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Floating top-right menu holding: user identity, agency switcher (when the
 * user is a member of 2+ agencies), Change password, Theme toggle, Sign out,
 * and an optional `extra` slot (e.g. the main-app sidebar-layout switcher).
 */
export default function UserMenu({ extra = null, positionClass = "fixed top-3 right-3 z-50" }) {
  const { user, logout, switchAgency } = useAuth();
  const nav = useNavigate();
  const themeCtx = useTheme();
  const [open, setOpen] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const rect = usePortalAnchorRect(open, triggerRef);
  // Memberships (email → 1..N agencies). Lazy-loaded the first time the
  // menu opens so we don't ping /auth/memberships on every route render.
  const [memberships, setMemberships] = useState(null);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [switching, setSwitching] = useState(false);

  const loadMemberships = useCallback(async () => {
    setMembershipsLoading(true);
    try {
      const r = await api.get("/auth/memberships");
      setMemberships(r.data || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("memberships fetch failed:", err);
      setMemberships([]);
    } finally {
      setMembershipsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    if (memberships === null && !membershipsLoading) loadMemberships();
    const onDoc = (e) => {
      const insideRoot = rootRef.current && rootRef.current.contains(e.target);
      const insidePanel = panelRef.current && panelRef.current.contains(e.target);
      if (!insideRoot && !insidePanel) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, memberships, membershipsLoading, loadMemberships]);

  if (!user) return null;

  const initials = (() => {
    const src = (user.name || user.email || "?").trim();
    const parts = src.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return src.slice(0, 2).toUpperCase();
  })();

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    nav("/login");
  };

  const handleSwitch = async (agencyId, agencyName) => {
    if (switching) return;
    setSwitching(true);
    try {
      await switchAgency(agencyId, agencyName);
      toast.success(`Switched to ${agencyName}`);
      setOpen(false);
      setMemberships(null);  // Force refresh next time — role may differ.
      // Landing route depends on the new membership's role. Simple safe
      // choice: go to `/` — the router redirects portal / guard users to
      // their own landing.
      nav("/");
      // Nudge everything on the page to refetch under the new agency scope.
      // The cheapest way is a hard reload; the alternative (revalidating
      // every fetch cache manually) is impractical.
      setTimeout(() => { window.location.reload(); }, 50);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not switch agency";
      toast.error(typeof detail === "string" ? detail : "Could not switch agency");
    } finally {
      setSwitching(false);
    }
  };

  const themeCfg = {
    light: { Icon: Moon, label: "Dark mode" },
    dark:  { Icon: MonitorSmartphone, label: "Auto (system)" },
    auto:  { Icon: Sun, label: `Light mode · currently ${themeCtx?.resolved || "auto"}` },
  }[themeCtx?.theme] || { Icon: Moon, label: "Dark mode" };

  return (
    <>
      <div ref={rootRef} className={positionClass} data-testid="user-menu-root">
        <button
          ref={triggerRef}
          type="button"
          data-testid="user-menu-trigger"
          onClick={() => setOpen((v) => !v)}
          title={`${user.name || user.email} · ${user.role || ""}`}
          className="size-10 rounded-full bg-[#121212] border border-[#27272A] hover:border-[#FF3B30] text-white text-[11px] font-bold tracking-widest flex items-center justify-center transition-colors shadow-lg"
        >
          {initials}
        </button>
        {open && rect && createPortal(
          <div
            ref={panelRef}
            data-testid="user-menu-dropdown"
            className="fixed z-[90] w-72 bg-[#0F0F12] border border-[#27272A] shadow-2xl"
            style={{
              // Anchor to the right edge of the trigger; clamp to viewport.
              top: rect.bottom + 8,
              left: Math.max(8, Math.min(rect.right - 288, window.innerWidth - 288 - 8)),
            }}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-[#27272A]">
              <div className="flex items-center gap-2 label-mute mb-1">
                <UserIcon className="size-3" /> Signed in
              </div>
              <div className="text-sm font-medium truncate" data-testid="user-menu-name">
                {user.name || user.email}
              </div>
              {user.email && user.name && (
                <div className="text-xs text-zinc-500 truncate font-mono mt-0.5">{user.email}</div>
              )}
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
                {user.role}
              </div>
            </div>

            {/* Optional extra slot (e.g. sidebar layout switcher) */}
            {extra && (
              <div className="px-4 py-3 border-b border-[#27272A]" data-testid="user-menu-extra">
                {extra}
              </div>
            )}

            {/* Switch agency — visible only when the user has 2+ live
                memberships across the platform. Silent (nothing rendered)
                for the single-tenant common case. */}
            {memberships && memberships.length >= 2 && (
              <div className="px-4 py-3 border-b border-[#27272A]" data-testid="user-menu-switch-agency">
                <div className="flex items-center gap-2 label-mute mb-2">
                  <Building2 className="size-3" /> Switch agency
                </div>
                <ul className="space-y-1">
                  {memberships.map((m) => (
                    <li key={`${m.agency_id}-${m.user_id}`}>
                      <button
                        type="button"
                        disabled={m.is_current || switching}
                        onClick={() => handleSwitch(m.agency_id, m.agency_name)}
                        data-testid={`switch-agency-${m.agency_id || "no-tenant"}`}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs transition-colors ${
                          m.is_current
                            ? "bg-[#1a1a1a] text-white cursor-default"
                            : "text-zinc-300 hover:bg-[#121212] hover:text-white"
                        }`}
                      >
                        <div className="flex-1 text-left min-w-0">
                          <div className="font-semibold truncate">{m.agency_name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                            {(m.role || "").replace(/_/g, " ")}
                          </div>
                        </div>
                        {m.is_current ? (
                          <Check className="size-3.5 text-[#34C759] shrink-0" />
                        ) : (
                          <RefreshCw className="size-3 text-zinc-500 shrink-0" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="p-2 flex flex-col">
              <button
                type="button"
                data-testid="user-menu-change-password"
                onClick={() => { setShowChangePw(true); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-white hover:bg-[#121212] transition-colors"
              >
                <KeyRound className="size-3.5" /> Change password
              </button>
              {themeCtx && (
                <button
                  type="button"
                  data-testid="user-menu-theme-toggle"
                  onClick={() => themeCtx.toggle()}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-white hover:bg-[#121212] transition-colors"
                >
                  <themeCfg.Icon className="size-3.5" />
                  {themeCfg.label}
                </button>
              )}
              <button
                type="button"
                data-testid="user-menu-logout"
                onClick={handleLogout}
                className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest border border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
              >
                <LogOut className="size-3.5" /> Sign out
              </button>
            </div>
          </div>,
          document.body,
        )}
      </div>
      {showChangePw && (
        <ChangePasswordModal onClose={() => setShowChangePw(false)} />
      )}
    </>
  );
}
